import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { PasswordModule } from 'primeng/password';

import { AccountCentreService } from '../../../../account-centre/account-centre.service';
import { UpdatePasswordRequest } from '../../../../models/account-centre/update-password-request.model';
import { passwordValidator } from '../../../../utility/passwordValidator';

@Component({
  selector: 'app-password-security-card',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PasswordModule],
  templateUrl: './password-security-card.component.html',
  styleUrl: './password-security-card.component.scss'
})
export class PasswordSecurityCardComponent implements OnInit {
  updatePasswordForm!: FormGroup;
  submitted = false;
  loading = false;
  // Holds error message from the API to show under the current password field
  currentPasswordError: string | null = null;
  lastUpdatedText = 'Added on May 27, 2025';

  constructor(
    private fb: FormBuilder,
    private accountCentreService: AccountCentreService,
    private messageService: MessageService
  ) {
    this.buildForm();
  }

  ngOnInit() {}

  buildForm() {
    this.updatePasswordForm = this.fb.group({
      oldPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required]]
    }, { validators: this.passwordsMatchValidator });
  }

  passwordsMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  onSubmit() {
    this.submitted = true;
    this.currentPasswordError = null;

    if (this.updatePasswordForm.invalid) {
      return;
    }

    if (!this.updatePasswordForm.dirty) {
      this.messageService.add({ severity: 'info', summary: 'Info', detail: 'No changes detected.' });
      return;
    }

    this.loading = true;
    const { oldPassword, newPassword } = this.updatePasswordForm.value;

    this.accountCentreService.updatePassword({ oldPassword, newPassword } as UpdatePasswordRequest).subscribe({
      next: () => {
        this.loading = false;
        this.updatePasswordForm.reset();
        this.submitted = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Password updated successfully.' });
      },
      error: (err: any) => {
        this.loading = false;
        let errorMsg = 'password doesnot match'; // Fallback as requested
        
        if (err?.error?.errors) {
          const errorsObj = err.error.errors;
          if (Array.isArray(errorsObj)) {
            const passwordMismatch = errorsObj.find(e => e.errorCode === 'PasswordMismatch');
            if (passwordMismatch) {
              errorMsg = 'password doesnot match';
            } else if (errorsObj.length > 0) {
              errorMsg = errorsObj[0].message || 'password doesnot match';
            }
          } else if (typeof errorsObj === 'object') {
            // Dictionary format from .NET
            const keys = Object.keys(errorsObj);
            if (keys.length > 0) {
              const firstKey = keys[0];
              if (Array.isArray(errorsObj[firstKey])) {
                errorMsg = errorsObj[firstKey][0];
              } else {
                errorMsg = errorsObj[firstKey];
              }
              // If the backend indicates an oldPassword error specifically
              if (firstKey.toLowerCase().includes('oldpassword') || firstKey.toLowerCase().includes('passwordmismatch')) {
                errorMsg = 'password doesnot match';
              }
            }
          }
        } else if (err?.error?.message) {
          // If message is generic, use the requested fallback
          if (err.error.message.toLowerCase().includes('failed to update')) {
            errorMsg = 'password doesnot match';
          } else {
            errorMsg = err.error.message;
          }
        }

        this.currentPasswordError = errorMsg;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: this.currentPasswordError ?? undefined });
      }
    });
  }
}
